import React, {useState} from "react";
import {Dialog, IconButton, Button, TextField, CircularProgress} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import * as axios from "axios";
import "./CreditOrderModal.css";

const baseUrl = 'https://api.appetito.ro/';

function money(value) {
  return (parseFloat(value) || 0).toFixed(2);
}

// order carries whatever OrderDetailsModal currently has for it — including
// total_price/credited_total, since those come from the same GET
// /v1/admin/order/:id response this modal's POST also returns, keeping both
// in sync without a full refetch.
export default function CreditOrderModal({order, onClose, onCredited}) {
  const [amount, setAmount] = useState(() => remainingFor(order));
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMessage, setErrorMessage] = useState('');

  if (!order) {
    return null;
  }

  const isCanceled = order.status === 'canceled';
  const remaining = remainingFor(order);
  const creditAmount = isCanceled ? remaining : amount;

  const confirmCredit = async () => {
    setStatus('loading');
    const token = localStorage.getItem('access_token');
    try {
      const response = await axios.default.post(`${baseUrl}v1/admin/order/${order.order_id}/credit`, {
        amount: creditAmount,
      }, {
        headers: {Authorization: `Bearer ${token}`}
      });
      setStatus('success');
      onCredited(response.data);
    } catch (e) {
      setStatus('error');
      setErrorMessage(e.response?.data?.message || 'A apărut o eroare, încearcă din nou.');
    }
  };

  return (
    <Dialog open={!!order} onClose={status === 'loading' ? undefined : onClose} fullWidth maxWidth="xs">
      <div className="credit-modal">
        <div className="credit-modal-topbar">
          <span className="credit-modal-title">Creditare comandă #{order.order_id}</span>
          <IconButton className="credit-modal-close" size="small" onClick={onClose} disabled={status === 'loading'}>
            <CloseIcon fontSize="small"/>
          </IconButton>
        </div>

        {status === 'success' ? (
          <>
            <div className="credit-modal-message credit-modal-message--success">
              Comanda a fost creditată cu suma de {money(creditAmount)} lei.
            </div>
            <div className="credit-modal-actions">
              <Button variant="contained" color="primary" onClick={onClose}>Închide</Button>
            </div>
          </>
        ) : (
          <>
            {isCanceled ? (
              <div className="credit-modal-message">
                Comanda este anulată — clientul va fi creditat cu suma integrală de <strong>{money(remaining)} lei</strong>.
              </div>
            ) : (
              <>
                <div className="credit-modal-message">
                  Introdu suma cu care va fi creditat clientul (implicit suma rămasă din comandă).
                </div>
                <TextField
                  type="number"
                  label="Sumă (lei)"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  inputProps={{min: 0.01, max: remaining, step: 0.01}}
                  fullWidth
                  margin="dense"
                  disabled={status === 'loading'}
                />
              </>
            )}

            {status === 'error' && (
              <div className="credit-modal-message credit-modal-message--error">{errorMessage}</div>
            )}

            <div className="credit-modal-actions">
              <Button onClick={onClose} disabled={status === 'loading'}>Anulează</Button>
              <Button
                variant="contained"
                color="primary"
                onClick={confirmCredit}
                disabled={status === 'loading' || !(parseFloat(creditAmount) > 0) || parseFloat(creditAmount) > remaining + 0.01}
              >
                {status === 'loading' ? <CircularProgress size={18} color="inherit"/> : 'Confirmă'}
              </Button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}

// Rounded to cents — total_price/credited_total are both sums of float64
// additions/subtractions on the backend, so the raw difference can come out
// as e.g. 61.970000000000006 and leak straight into the input's value.
function remainingFor(order) {
  if (!order) {
    return 0;
  }
  const raw = Math.max(0, (parseFloat(order.total_price) || 0) - (parseFloat(order.credited_total) || 0));
  return Math.round(raw * 100) / 100;
}
